import { merge } from 'lodash-es';
import type { ComputedRef } from 'vue';
import type { PassengerKey } from '@aviata/booking-data';
import type { Airflow } from '@aviata/platform-types';

import { providerClassLib } from '../../provider-class';
import {
  getBaseValidations,
  getBaseValidationsForSMProvider,
} from './base-validations';
import { getSchemaValidations } from './schema-validations';

type ValidationArgs = {
  passengerKey?: PassengerKey;
  validationMessagePath: string;
  citizenship: ComputedRef<string>;
  departureDate?: ComputedRef<string | undefined>;
  schema: Airflow.Offer.Schema.PassengerEntity;
  providerClass?: string;
};

/**
 * Method for create validation rules for a passenger with default rules and schema.
 * To use validation messages add translations of every validation rule to your ru/kk.json files
 *
 * @param validationMessagePath - prefix of all validations in your ru/kk.json locales
 *
 * @example
 *
 * ```js
 * const validationRules = initValidation({
 *    schema: ...,
      citizenship: ...,
      passengerKey: ...,
      validationMessagePath: 'booking.validations'
 * });
 * ```
 * **.../ru.json**
 * ```json
 * {
 *  "booking": {
  *   "validations": {
        "required": "Обязательное поле",
        "alpha": "Используйте только латинские буквы",
        "isValidDob": "Некорректно указана дата рождения",
        "docMinLength": "№ Документа не может содержать менее 5 символов",
        "isValidDocExpiration": "Некорректно указан срок действия документа",
        "iinMinLength": "ИИН состоит из 12 цифр",
        "isInvalidIin": "Неверный ИИН",
        "schema": {
          "firstNameMinLength": "Имя не может состоять меньше чем, из {min} букв",
          "firstNameMaxLength": "Имя не может состоять больше чем, из {max} букв",
          "lastNameMinLength": "Фамилия не может состоять меньше чем, из {min} букв",
          "lastNameMaxLength": "Фамилия не может состоять больше чем, из {max} букв",
          "agePrefix": "На момент вылета",
          "youthPrefix": "Возраст пассажира по тарифу «Молодежный» должен быть",
          "minAge": {
            "ADT": "@:booking.validations.schema.agePrefix взрослый должен быть старше {min} лет",
            "CHD": "@:booking.validations.schema.agePrefix ребенку должно быть больше {min} лет и меньше {max} лет",
            "INF": "@:booking.validations.schema.agePrefix младенцу должно быть больше {min} лет",
            "YTH": "@:booking.validations.schema.youthPrefix от {min} до {max} лет"
          },
          "maxAge": {
            "ADT": "@:booking.validations.schema.agePrefix взрослый должен быть моложе {max} лет",
            "CHD": "@:booking.validations.schema.agePrefix ребенку должно быть меньше {max} лет",
            "INF": "@:booking.validations.schema.agePrefix младенцу должно быть меньше {max} лет",
            "YTH": "@:booking.validations.schema.youthPrefix меньше {max} лет"
          }
        }
      }
    }
 * }
 * ```
 */
export const initValidation = ({
  schema,
  citizenship,
  passengerKey,
  departureDate,
  validationMessagePath,
  providerClass,
}: ValidationArgs) => {
  const baseValidationRules = getBaseValidations(
    citizenship,
    departureDate,
    validationMessagePath,
  );

  const baseValidationRulesForSMProvider = providerClassLib.isFunSun(
    providerClass ?? '',
  )
    ? getBaseValidationsForSMProvider(
        citizenship,
        departureDate,
        validationMessagePath,
      )
    : {};

  const schemaValidation = getSchemaValidations({
    passengerKey,
    passengerSchema: schema,
    messagePath: validationMessagePath,
  });

  return merge(
    baseValidationRules,
    schemaValidation,
    baseValidationRulesForSMProvider,
  );
};

export { getBaseValidations };
export { IIN_LENGTH, isValidIin, isValidIinDate } from './iin';
